import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom"; // Import Link for navigation
import { Brain, Edit, Users, Grid, Target, CheckCircle, Lightbulb, TrendingUp } from "lucide-react";

// --- FRAMER MOTION VARIANTS ---
const pageVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
        opacity: 1, 
        y: 0, 
        transition: { type: "spring", stiffness: 80, damping: 12, delay: 0.1 } 
    },
};

const skillCardVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
        opacity: 1, 
        x: 0, 
        transition: { duration: 0.5, type: "tween" }
    },
};

// --- SKILL DOMAIN CARD COMPONENT ---
const SkillDomainCard = ({ icon, title, goals, colorClass }) => (
    <motion.div 
        className="bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-100 flex flex-col h-full transition-shadow duration-300 hover:shadow-2xl"
        variants={skillCardVariants}
    >
        <div className="flex items-center gap-4 mb-4">
            <div className={`p-3 rounded-full flex-shrink-0 ${colorClass.bg} ${colorClass.text}`}>
                {icon}
            </div>
            <h3 className="text-xl md:text-2xl font-bold text-gray-800">
                {title}
            </h3>
        </div>
        
        <ul className="space-y-4 flex-grow list-none p-0 m-0 border-l-4 border-gray-100 pl-4 mt-2">
            {goals.map((goal, index) => (
                <li key={index} className="flex items-start text-gray-700 text-base leading-relaxed">
                    <CheckCircle className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5 mr-3" />
                    <span>{goal}</span>
                </li>
            ))}
        </ul>
    </motion.div>
);

// --- MAIN SKILL DEVELOPMENT PAGE COMPONENT ---
export default function SkillDevelopmentPage() {
    
    // Data for the skill domain cards
    const skillDomainsData = [
        {
            icon: <Brain className="w-6 h-6" />,
            title: "Critical Thinking & Analysis",
            colorClass: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
            goals: [
                "Source Verification: Learn to question information and identify bias or misinformation.",
                "Logical Deduction: Practice forming coherent arguments based on evidence, not emotion.",
                "Scenario Mapping: Use online tools for cause-and-effect thinking and complex decision-making.",
            ],
        },
        {
            icon: <Edit className="w-6 h-6" />,
            title: "Digital Creativity & Innovation",
            colorClass: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
            goals: [
                "Media Production: Master tools for video editing, graphic design, and podcast creation.",
                "Prototyping: Use digital platforms (like Tinkercad) to design and iterate on ideas.",
                "Creative Coding: Introduce basic programming to build games or interactive stories.",
            ],
        },
        {
            icon: <Users className="w-6 h-6" />,
            title: "Effective Collaboration & Ethics",
            colorClass: { bg: 'bg-green-100', text: 'text-green-600' },
            goals: [
                "Team Platforms: Efficiently use shared documents and project management software.",
                "Digital Etiquette: Teach respectful communication and professional conduct in virtual teams.",
                "Intellectual Property: Understand copyright, fair use, and proper citation of digital resources.",
            ],
        },
        {
            icon: <Target className="w-6 h-6" />,
            title: "Problem Solving & Resilience",
            colorClass: { bg: 'bg-red-100', text: 'text-red-600' },
            goals: [
                "Debugging Mindset: Treat technical errors as puzzles to solve, encouraging tenacity.",
                "Resourcefulness: Train them to independently find solutions using search engines and forums.",
                "Task Decomposition: Break down large, complex projects into smaller, manageable steps.",
            ],
        },
    ];

    return (
        <div className="min-h-screen p-6 sm:p-12 bg-gradient-to-br from-gray-50 via-indigo-50 to-pink-50 font-sans">
            <motion.div 
                className="max-w-6xl mx-auto bg-white p-6 sm:p-12 rounded-3xl shadow-2xl border-t-6 border-indigo-600"
                variants={pageVariants}
                initial="hidden"
                animate="visible"
            >
                {/* === PAGE HEADER === */}
                <header className="text-center mb-14">
                    <div className="inline-block p-4 bg-indigo-600 rounded-full shadow-lg mb-4">
                        <Lightbulb className="w-10 h-10 text-white" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-gray-800 tracking-tight leading-tight">
                        Future-Ready Skill Development
                    </h1>
                    <p className="text-gray-500 mt-4 text-xl">
                        A framework for cultivating essential 21st-century skills through guided digital exploration.
                    </p>
                </header>

                {/* --- SKILLS GRID (Responsive 2x2 layout) --- */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
                    {skillDomainsData.map((skill, index) => (
                        <motion.div
                            key={index}
                            initial="hidden"
                            animate="visible"
                            variants={skillCardVariants}
                            custom={index}
                            // Stagger animation for visual flow
                            transition={{ delay: 0.3 + index * 0.15 }} 
                        >
                            <SkillDomainCard {...skill} />
                        </motion.div>
                    ))}
                </div>
                
                {/* --- FOOTER CTA --- */}
                <motion.div 
                    className="mt-20 text-center p-8 sm:p-10 bg-indigo-50 rounded-xl border-2 border-indigo-200 shadow-inner"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.2, duration: 0.6 }}
                >
                    <p className="text-xl md:text-2xl font-bold text-gray-800 mb-4 flex items-center justify-center gap-3">
                        <TrendingUp className="w-7 h-7 text-indigo-600"/>
                        Ready to connect skill development with controlled usage?
                    </p>
                    {/* Use React Router Link to navigate to the Login page */}
                    <Link 
                        to="/login" 
                        className="inline-block px-10 py-4 bg-indigo-600 text-white font-extrabold rounded-lg hover:bg-indigo-700 transition-colors shadow-2xl shadow-indigo-300/50 text-lg uppercase tracking-widest"
                    >
                        Go to Control Panel Login
                    </Link>
                </motion.div>

            </motion.div>
        </div>
    );
}